/**
 * A gateway's status today, in one chip with a tooltip that says why:
 * blocked (country-wide or in N containers, the verdict's reason) · send
 * failing (SMSWiser refused a send today — the last message) · winner
 * (of which route test, with the sender) · testing (a route test sends
 * through it) · past limits (failing today, not blocked yet) · ok.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { Badge, Tooltip } from '@/components/ui';

import { type GatewayToday, LIMITS, pastLimits } from '../gateways-today';

const words = (g: GatewayToday): { tone: 'destructive' | 'success' | 'info' | 'warning' | 'secondary'; label: string; why: string } => {
  if (g.status === 'blocked') {
    const where = g.countryBlocked ? 'blocked today in every container' : `blocked today in ${g.blocks.length} container${g.blocks.length === 1 ? '' : 's'}`;
    const reasons = [...new Set(g.blocks.map((b) => `${b.containerName}: ${b.reason}`))].join(' · ');
    return { tone: 'destructive', label: g.countryBlocked ? 'blocked · country' : 'blocked', why: `${where} — ${reasons}. Tomorrow it is back in the pool.` };
  }
  if (g.status === 'failing') {
    const n = g.failures.length;
    return {
      tone: 'destructive',
      label: 'send failing',
      why: `${n} send${n === 1 ? '' : 's'} refused by SMSWiser today — last: ${g.failures[0].message}. Auto-send retries each minute; refusals in a row halt it. The Errors tab has every one.`,
    };
  }
  if (g.status === 'winner') {
    const wins = g.winnerFor.map((w) => `${w.masterName} (sender ${w.sender}, applied to ${w.appliedTo.length} container${w.appliedTo.length === 1 ? '' : 's'})`).join(' · ');
    return { tone: 'success', label: 'winner', why: `Won today's route test of ${wins} — the rest of their day goes through it.` };
  }
  if (g.status === 'testing') return { tone: 'info', label: 'testing', why: 'A route test sends test drops through it today; the winner is picked when the test is over.' };
  if (pastLimits(g.rates))
    return {
      tone: 'warning',
      label: 'past limits',
      why: `Today's rates are past the usual verdict limits (delivered under ${LIMITS.deliveredBelow * 100} % or bot clicks over ${LIMITS.botAbove * 100} %). Not blocked yet — the gateway verdict decides at the next pass.`,
    };
  return { tone: 'secondary', label: 'ok', why: 'Sends as planned — no block, no test.' };
};

export const StatusChip: FC<{ gateway: GatewayToday }> = ({ gateway }) => {
  const w = words(gateway);
  return (
    <Tooltip content={w.why}>
      <Badge tone={w.tone}>{w.label}</Badge>
    </Tooltip>
  );
};
